import { useCallback } from 'react';

import { NfzDepartment } from '@dreryk/egabinet-nfz-configuration-frontend-api';
import { NfzDepartmentForPatientQuery } from '@dreryk/egabinet-patient-frontend-api';

import { UseCqrsQueryResult, useCqrsSearch } from '@dreryk/command-query-react-common';
import { createSingleInterpolatedTranslationKey, useFormController, useTranslate } from '@dreryk/react-common';
import {
   dispatchSnackbarNegativeEvent,
   LocationBackState,
   useHistoryPushWithBackAction,
} from '@dreryk/react-components';

import { patientPaths } from '~libs.desktop.shared.paths';

import { patientTranslationKeys, personalDataTranslationKeys } from '../translations';

import { PatientFileLocationData, PersonalDataFormBean, PersonalDataFormController } from './common.types';
import { PatientSelectIdentifierType } from './PersonForm/PatientIdentifierTypeField/PatientIdentifierTypeField';

export const usePersonalDataFormController = (formBean: PersonalDataFormBean): PersonalDataFormController => {
   return useFormController<PersonalDataFormBean>(formBean);
};

export const useGetNfzDepartmentsQuery = (): UseCqrsQueryResult<NfzDepartment[]> => {
   return useCqrsSearch(() => new NfzDepartmentForPatientQuery());
};

type GoToPatientFileLocationData = Record<string, unknown>;
export const useGoToPatientFile = <DATA extends GoToPatientFileLocationData | unknown = unknown>(
   addToStack?: boolean
): ((
   patientId: number,
   state?: LocationBackState<PatientFileLocationData & DATA>,
   showAppointmentOutOfSchedule?: boolean
) => void) => {
   const push = useHistoryPushWithBackAction<PatientFileLocationData>(addToStack ?? false);
   return useCallback(
      (patientId, state, showAppointmentOutOfSchedule) => {
         const { data, ...restState } = state ?? {};
         push(patientPaths.getDataPath(patientId), {
            data: {
               ...(data ?? {}),
               showAppointmentOutOfSchedule: showAppointmentOutOfSchedule ?? true,
            },
            ...restState,
         });
      },
      [push]
   );
};

type PatientAlreadyExistsFaultHandler = (
   __: unknown,
   parameters: {
      personalIdentifierType: PatientSelectIdentifierType;
   }
) => void;

export const usePatientAlreadyExistsFaultHandler = (): PatientAlreadyExistsFaultHandler => {
   const translate = useTranslate();

   return useCallback(
      (__, parameters): void => {
         if (parameters.personalIdentifierType) {
            dispatchSnackbarNegativeEvent(
               createSingleInterpolatedTranslationKey(
                  patientTranslationKeys.patientAlreadyExists,
                  'identifier',
                  translate(personalDataTranslationKeys.identifierType.faultMap[parameters.personalIdentifierType.id])
               )
            );
         }
      },
      [translate]
   );
};
